import { Guardrails } from './guardrails.js';

/** Sections of the agent guardrails form, as dotted paths into `Guardrails`. */
export const GUARDRAIL_SECTIONS = {
  limits: ['maxRunTokens', 'maxQueuedTasks', 'maxSubagentDepth', 'maxSubagents', 'approvalTtlHours'],
  network: ['egressAllowlist'],
  fallback: [
    'claudeCode.codexFallbackModelRef',
    'claudeCode.codexFallbackReasoningEffort',
    'codex.claudeFallbackModelRef',
    'codex.claudeFallbackReasoningEffort',
  ],
  sandbox: ['git', 'sandboxLimits', 'claudeCode.sandbox', 'codex.sandbox'],
  delegation: ['delegateTo'],
  messages: ['readLateMessages', 'respondWhenMentioned'],
  ruleOfTwo: ['ruleOfTwo'],
} as const;

/** Guardrails are plain JSON, so a JSON round trip is a complete deep copy. */
const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

const norm = (v: unknown): unknown => (v === undefined ? null : v);

function equal(a: unknown, b: unknown): boolean {
  a = norm(a);
  b = norm(b);
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ak = Object.keys(a as object).filter((k) => norm((a as Record<string, unknown>)[k]) !== null);
  const bk = Object.keys(b as object).filter((k) => norm((b as Record<string, unknown>)[k]) !== null);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => equal((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

function get(o: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce<unknown>(
      (x, k) => (x && typeof x === 'object' ? (x as Record<string, unknown>)[k] : undefined),
      o,
    );
}

/** The paths whose value differs from the platform default (`Guardrails.parse({})`). */
export function changedGuardrailPaths(
  value: Guardrails,
  paths: readonly string[],
  defaults: Guardrails = Guardrails.parse({}),
): string[] {
  return paths.filter((p) => !equal(get(value, p), get(defaults, p)));
}

/** A copy of `value` with the given paths set back to their defaults. */
export function resetGuardrailPaths(
  value: Guardrails,
  paths: readonly string[],
  defaults: Guardrails = Guardrails.parse({}),
): Guardrails {
  const out = clone(value) as Record<string, unknown>;
  for (const p of paths) {
    const keys = p.split('.');
    const last = keys.pop()!;
    let target = out;
    for (const k of keys) {
      if (!target[k] || typeof target[k] !== 'object') target[k] = {};
      target = target[k] as Record<string, unknown>;
    }
    const d = get(defaults, p);
    if (d === undefined) delete target[last];
    else target[last] = clone(d);
  }
  return Guardrails.parse(out);
}
